import type { IssueDetail } from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIssueMgmtWrite, useIssueThemes } from '../../hooks/useIssueMgmt.js';
import type { SaveIssueFields } from './useIssueFieldSave.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * テーマの名前の上限字数（API と同じ）。
 * @graph-connects none
 */
const THEME_NAME_MAX_LENGTH = 40;

/**
 * 課題のテーマ（複数付けられるタグ）。編集キーの人は外す・付ける・新しく作って付けることができる。
 * 終了したテーマは付ける候補に出さない（付いているものは残る）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題に付いているテーマを見せ、編集キーの人がテーマを付ける・外す・新しく作って付けられるようにする
 * @graph-connects useIssueThemes [calls] テーマの一覧
 * @graph-connects useIssueMgmtWrite [calls] createTheme
 */
export function IssueThemeEditor({
  issue,
  canEdit,
  save,
}: {
  issue: IssueDetail;
  canEdit: boolean;
  save: SaveIssueFields;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const themes = useIssueThemes();
  const createTheme = useIssueMgmtWrite('createTheme');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const current = issue.themes.map((theme) => theme.id);
  const candidates = (themes.data?.themes ?? []).filter(
    (theme) => theme.status === 'active' && !current.includes(theme.id),
  );

  const saveThemes = (next: string[]) =>
    save(
      [{ field: 'themeIds', base: current, value: next }],
      t('issueMgmt.others.themesSaved', { title: issue.title }),
    );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {issue.themes.length === 0 && !canEdit && (
          <span className="text-sm text-[var(--color-text-muted)]">
            {t('issueMgmt.common.dash')}
          </span>
        )}
        {issue.themes.map((theme) => (
          <span
            key={theme.id}
            className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-2.5 py-0.5 text-xs text-[var(--color-text-primary)]"
          >
            {theme.name}
            {canEdit && (
              <button
                type="button"
                aria-label={t('issueMgmt.others.themeRemove', { name: theme.name })}
                onClick={() => {
                  void saveThemes(current.filter((id) => id !== theme.id));
                }}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            )}
          </span>
        ))}
        {canEdit && !adding && (
          <button
            type="button"
            onClick={() => {
              setAdding(true);
            }}
            className="text-xs font-medium text-[var(--color-accent-primary)] hover:underline"
          >
            {t('issueMgmt.others.addTheme')}
          </button>
        )}
      </div>
      {canEdit && adding && (
        <div className="space-y-2 rounded-lg border border-[var(--color-border)] p-3">
          <div
            role="group"
            aria-label={t('issueMgmt.others.themePick')}
            className="flex flex-wrap gap-2"
          >
            {candidates.map((theme) => (
              <button
                key={theme.id}
                type="button"
                onClick={() => {
                  void saveThemes([...current, theme.id]).then((saved) => {
                    if (saved) setAdding(false);
                  });
                }}
                className="rounded-full border border-[var(--color-border)] px-2.5 py-0.5 text-xs hover:border-[var(--color-accent-primary)]"
              >
                {theme.name}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input
              aria-label={t('issueMgmt.others.themeNew')}
              placeholder={t('issueMgmt.others.themeNew')}
              value={newName}
              maxLength={THEME_NAME_MAX_LENGTH}
              onChange={(e) => {
                setNewName(e.target.value);
              }}
            />
            <Button
              size="sm"
              disabled={newName.trim() === ''}
              loading={createTheme.isPending}
              onClick={() => {
                createTheme.mutate(
                  { name: newName.trim(), description: null },
                  {
                    onSuccess: (created) => {
                      void saveThemes([...current, created.id]).then((saved) => {
                        if (saved) {
                          setNewName('');
                          setAdding(false);
                        }
                      });
                    },
                    onError: toast.notifyError,
                  },
                );
              }}
            >
              {t('issueMgmt.others.themeCreate')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAdding(false);
              }}
            >
              {t('issueMgmt.common.cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
